import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar, Cpu, Monitor, Tag, ThumbsUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { writeupPath } from "@/lib/slug";

const PLATFORM_STYLES = {
  htb: {
    label: "Hack The Box",
    className: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
  },
  proving_grounds: {
    label: "Proving Grounds",
    className: "border-cyan-400/30 bg-cyan-400/10 text-cyan-300"
  },
  other: {
    label: "Other",
    className: "border-slate-500/30 bg-slate-500/10 text-slate-300"
  }
};

const DIFFICULTY_STYLES = {
  easy: "border-emerald-400/35 bg-emerald-400/12 text-emerald-300",
  medium: "border-amber-400/35 bg-amber-400/12 text-amber-300",
  hard: "border-rose-400/35 bg-rose-400/12 text-rose-300",
  insane: "border-fuchsia-400/35 bg-fuchsia-400/12 text-fuchsia-300"
};

const platformLabel = (platform) => PLATFORM_STYLES[platform]?.label || platform || "Unknown";

const difficultyLabel = (difficulty) =>
  difficulty ? difficulty.charAt(0).toUpperCase() + difficulty.slice(1) : "Unknown";

const formatDate = (dateString) => {
  if (!dateString) return "Recently added";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  } catch {
    return "Recently added";
  }
};

const getOsIcon = (os) => {
  if (os === "windows") return <Monitor className="h-3.5 w-3.5" />;
  return <Cpu className="h-3.5 w-3.5" />;
};

const countWords = (content = "") => {
  const clean = typeof content === "string" ? content.trim() : "";
  if (!clean) return null;
  return Math.max(1, Math.round(clean.split(/\s+/).length / 220));
};

const WriteupCard = ({ writeup, featured = false }) => {
  const coverImage = writeup.cover_image;
  const readTime = countWords(writeup.content);

  // Only show a metric tile when it actually has a value. Zero/empty
  // values are hidden rather than rendered as "0" — empty stats read as broken.
  //
  // View counts are deliberately NOT surfaced. A public counter showing single
  // digits is negative social proof: it tells the reader nobody else found this
  // useful. Traffic belongs in analytics, not on the card.
  const stats = [
    Number(writeup.upvotes) > 0 && { icon: ThumbsUp, value: writeup.upvotes, label: "upvotes" },
    readTime && { icon: Calendar, value: `${readTime} min`, label: "read time" },
    writeup.created_at && { icon: Calendar, value: formatDate(writeup.created_at), label: "published" },
  ].filter(Boolean);
  const tags = Array.isArray(writeup.tags) ? writeup.tags : [];
  const skills = Array.isArray(writeup.skills) ? writeup.skills : [];
  const cves = Array.isArray(writeup.cves) ? writeup.cves : [];
  const platform = PLATFORM_STYLES[writeup.platform] || PLATFORM_STYLES.other;
  const difficultyClass = DIFFICULTY_STYLES[writeup.difficulty] || DIFFICULTY_STYLES.easy;
  const renderStatValue = (label, value) => {
    if (label === "published" && typeof value === "string") {
      const [month, day = "", year = ""] = value.split(" ");
      return (
        <div className="flex min-h-[56px] flex-col justify-end leading-tight">
          <span>{month}</span>
          <span>{day.replace(",", "")},</span>
          <span>{year}</span>
        </div>
      );
    }

    if (label === "read time" && typeof value === "string") {
      const [amount, unit = ""] = value.split(" ");
      return (
        <div className="flex min-h-[56px] flex-col justify-end leading-tight">
          <span>{amount}</span>
          <span>{unit}</span>
        </div>
      );
    }

    return <span className="flex min-h-[56px] items-end">{value}</span>;
  };

  return (
    <Link
      to={writeupPath(writeup)}
      className={[
        "card-writeup group relative overflow-hidden border border-border/70 bg-[#0b1016]/95 transition-all duration-300",
        "hover:-translate-y-1 hover:border-accent-primary/35 hover:shadow-[0_24px_70px_rgba(0,255,136,0.08)]",
        featured ? "md:col-span-2" : ""
      ].join(" ")}
      data-testid={`writeup-card-${writeup.id}`}
    >
      <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(0,255,136,0.12),transparent_45%)]" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent-primary/40 to-transparent" />
      </div>

      {coverImage && (
        <div className="relative h-48 overflow-hidden border-b border-border/80">
          <img
            src={coverImage}
            alt={writeup.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#08110d] via-[#08110d]/45 to-transparent" />
          <div className="absolute left-4 top-4 flex flex-wrap gap-2">
            <Badge variant="outline" className={`text-[10px] uppercase tracking-[0.2em] ${difficultyClass}`}>
              {difficultyLabel(writeup.difficulty)}
            </Badge>
            <Badge variant="outline" className={`text-[10px] uppercase tracking-[0.18em] ${platform.className}`}>
              {platformLabel(writeup.platform)}
            </Badge>
          </div>
          {writeup.os_type && (
            <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/35 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.16em] text-slate-200 backdrop-blur">
              {getOsIcon(writeup.os_type)}
              <span>{writeup.os_type}</span>
            </div>
          )}
        </div>
      )}

      <div className="relative z-10 flex h-full flex-col p-5">
        {!coverImage && (
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Badge variant="outline" className={`text-[10px] uppercase tracking-[0.2em] ${difficultyClass}`}>
              {difficultyLabel(writeup.difficulty)}
            </Badge>
            <Badge variant="outline" className={`text-[10px] uppercase tracking-[0.18em] ${platform.className}`}>
              {platformLabel(writeup.platform)}
            </Badge>
            {writeup.os_type && (
              <Badge variant="outline" className="border-slate-500/30 bg-slate-500/10 text-[10px] uppercase tracking-[0.16em] text-slate-300">
                <span className="mr-1 inline-flex">{getOsIcon(writeup.os_type)}</span>
                {writeup.os_type}
              </Badge>
            )}
          </div>
        )}

        {stats.length > 0 && (
          <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-4">
            {stats.slice(0, 4).map(({ icon: Icon, value, label }) => (
              <div
                key={label}
                className="flex min-h-[96px] flex-col rounded-md border border-border/70 bg-[#0f151d] px-3 py-2"
              >
                <div className="mb-2 flex min-h-[28px] items-start gap-2 text-[10px] uppercase tracking-[0.18em] text-text-muted">
                  <Icon className="h-3 w-3" />
                  <span className="leading-[1.2]">{label}</span>
                </div>
                <div className="mt-auto font-mono text-sm text-text-primary">
                  {renderStatValue(label, value)}
                </div>
              </div>
            ))}
          </div>
        )}

        {writeup.machine_name && (
          <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.22em] text-accent-primary/85">
            // {writeup.machine_name}
          </p>
        )}

        <h3 className="mb-3 font-heading text-2xl font-bold leading-tight text-text-primary transition-colors duration-300 group-hover:text-accent-primary">
          {writeup.title}
        </h3>

        <p className="mb-5 line-clamp-3 text-sm leading-7 text-text-secondary">
          {writeup.description || "Walkthrough covering enumeration, exploitation, and privilege escalation paths."}
        </p>

        {skills.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {skills.slice(0, featured ? 5 : 4).map((skill) => (
              <span
                key={skill}
                className="rounded-full border border-accent-primary/20 bg-accent-primary/8 px-2.5 py-1 font-mono text-[11px] uppercase tracking-[0.12em] text-accent-primary/90"
              >
                {skill}
              </span>
            ))}
            {skills.length > (featured ? 5 : 4) && (
              <span className="rounded-full border border-border/80 px-2.5 py-1 font-mono text-[11px] uppercase tracking-[0.12em] text-text-muted">
                +{skills.length - (featured ? 5 : 4)} more
              </span>
            )}
          </div>
        )}

        {(cves.length > 0 || tags.length > 0) && (
          <div className="mb-6 flex flex-wrap gap-2">
            {cves.slice(0, 2).map((cve) => (
              <span
                key={cve}
                className="rounded-full border border-rose-400/25 bg-rose-400/10 px-2.5 py-1 font-mono text-[11px] uppercase tracking-[0.12em] text-rose-300"
              >
                {cve}
              </span>
            ))}
            {tags.slice(0, featured ? 4 : 3).map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1.5 rounded-full border border-border/70 px-2.5 py-1 font-mono text-[11px] uppercase tracking-[0.1em] text-text-muted"
              >
                <Tag className="h-3 w-3" />
                {tag}
              </span>
            ))}
          </div>
        )}

        <div className="mt-auto flex items-center justify-between border-t border-border/80 pt-4">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-text-muted">
            {platformLabel(writeup.platform)}
          </span>
          <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-accent-primary transition-transform duration-300 group-hover:translate-x-1">
            Open writeup
            <ArrowUpRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default WriteupCard;
